import React, { useEffect, useState, useMemo, useCallback } from 'react'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Table from 'react-bootstrap/Table'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import ButtonGroup from 'react-bootstrap/ButtonGroup'
import Dropdown from 'react-bootstrap/Dropdown'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import Page from 'layout/page'
import PageTitle from 'layout/page-title'
import { useDashboards } from 'api/hooks/use-dashboards'
import useSections from 'api/hooks/use-sections'
import TableLoading from 'shared/tables/table-loading'
import CreateMenu from 'dashboard/create-menu'
import UserContext from 'context/user-context'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Search from 'shared/forms/search'
import TableFooter from 'shared/tables/table-footer'
import { toast } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faList, faTh, faCheck, faLayerGroup, faUnlink, faEllipsisV, faFolder } from '@fortawesome/free-solid-svg-icons'
// `use-reports` is the LEGACY audit_reports resource — these are Samples, not Reports. The Report
// Builder lives in `use-report-builder`. Both export a `useReports`; alias both so the two can
// never be confused at a call site.
import useSamples from 'api/hooks/use-reports'
import { useReports as useBuilderReports } from 'api/hooks/use-report-builder'
import { useQueryClient } from 'react-query'
import { DndContext, PointerSensor, useSensor, useSensors, useDraggable } from '@dnd-kit/core'
import { useFolders, useMoveItem, useDestroyFolder, useUpdateFolder, useFetchRelatedItems } from 'api/hooks/use-folders'
import FolderCard from 'dashboard/folders/folder-card'
import FolderListRow from 'dashboard/folders/folder-list-row'
import CreateFolderModal from 'dashboard/folders/create-folder-modal'
import EditFolderModal from 'dashboard/folders/edit-folder-modal'
import ShareFolderModal from 'dashboard/folders/share-folder-modal'
import MoveToPrivateFolderModal from 'dashboard/folders/move-to-private-folder-modal'
import MoveLinkedItemsModal from 'dashboard/folders/move-linked-items-modal'
import FolderBreadcrumb from 'dashboard/folders/folder-breadcrumb'
import MutedText from 'shared/forms/muted-text'
import MoveFolderModal from 'dashboard/folders/move-folder-modal'
import MoveToFolderModal from 'dashboard/folders/move-to-folder-modal'
import {
  MAX_FOLDER_DEPTH,
  buildFolderTree,
  childFoldersOf,
  descendantFolders,
  folderAncestors,
  folderDepth,
  folderPath,
  isDescendantOf,
  rollupCount,
  subtreeHeight,
  canNestUnder
} from 'dashboard/folders/folder-tree'
import { isFolderDropId, parseFolderDropId } from 'dashboard/folders/folder-drag'
import { withFolderContext } from 'dashboard/folders/folder-context'
import ItemCard from 'dashboard/cards/item-card'
// Relative: `report-builder` has no path alias in vite.config.ts / jest.config.cjs.
import ReportActionsMenu from '../report-builder/report-actions-menu'
import ReportLockBadge from '../report-builder/report-lock-badge'
import ViewActionsMenu from 'dashboard/view-actions-menu'
import {
  ITEM_TYPES,
  toViewItem,
  toSampleItem,
  toReportItem,
  dragIdFor,
  parseDragId,
  typeKeyForApiType,
  matchesSectionFilter
} from 'dashboard/cards/item-shape'

// Content-type filter options. 'reports' is added only for beta users — /reports/:id is still
// beta-gated, so showing report cards to anyone else would hand them a card they cannot open.
const CONTENT_TYPES = {
  all: { label: 'All Items' },
  dashboards: { label: 'Views', icon: ITEM_TYPES.view.icon },
  samples: { label: 'Samples', icon: ITEM_TYPES.sample.icon },
  reports: { label: 'Reports', icon: ITEM_TYPES.report.icon }
}

// folders#move_item takes the items to bring along as {item_type, item_id}; the related-items
// endpoint hands them back keyed on `id`.
const relatedItemsParam = (relatedItems) =>
  (relatedItems?.length
    ? { related_items: relatedItems.map((related) => ({ item_type: related.item_type, item_id: related.id })) }
    : {})

const DraggableItem = ({ id, isMoving, disabled, isOwner, children }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    disabled: isMoving || disabled,
    data: { isOwner }
  })

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1000 : undefined
      }
    : undefined

  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      {isMoving ? (
        <div style={{ position: 'relative' }}>
          <div style={{ opacity: 0.3, pointerEvents: 'none' }}>{children}</div>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <span className="text-muted small">
              <span className="spinner-border spinner-border-sm me-1" role="status" />
              Moving...
            </span>
          </div>
        </div>
      ) : (
        children
      )}
    </div>
  )
}

// The list renders items as table rows, so the drag wiring has to land on the <tr> itself — the
// wrapping <div> DraggableItem uses is not valid inside <tbody> and drops the row out of the table.
const DraggableRow = ({ id, isMoving, disabled, isOwner, onClick, children }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    disabled: isMoving || disabled,
    data: { isOwner }
  })

  return (
    <tr
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={onClick}
      style={{ cursor: 'pointer', opacity: isDragging || isMoving ? 0.4 : 1 }}
    >
      {children}
    </tr>
  )
}

function Dashboard() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const user = React.useContext(UserContext)
  const activeFolderId = searchParams.get('folder_id')
  const canUseReports = !!user?.is_beta

  const defaultParams = {
    search: '',
    section_id: '',
    page: 1,
    perPage: 12
  }
  const [params, setParams] = React.useState(defaultParams)
  const [viewMode, setViewMode] = useState('grid')
  const [contentType, setContentType] = useState('all')
  const [selectedParentModule, setSelectedParentModule] = useState('')
  const [editingFolder, setEditingFolder] = useState(null)
  const [sharingFolderId, setSharingFolderId] = useState(null)
  const [movingItemIds, setMovingItemIds] = useState(new Set())
  const [movingFolderIds, setMovingFolderIds] = useState(new Set())
  const [movingFolder, setMovingFolder] = useState(null)
  const [pendingMove, setPendingMove] = useState(null)
  const [pendingLinkedMove, setPendingLinkedMove] = useState(null)
  const [movingItem, setMovingItem] = useState(null)

  const folderItemCount = useCallback((folder) => {
    if (!folder) return 0
    return (
      (folder.dashboards_count || 0) +
      (folder.audit_reports_count || 0) +
      (canUseReports ? folder.reports_count || 0 : 0)
    )
  }, [canUseReports])

  // The count that matters under the current filters: one type when a type tab is active, the
  // combined total otherwise. Counts arrive from the API already scoped to any section filter, and
  // count only what sits directly in the folder.
  const directFolderItemCount = useCallback((folder) => {
    if (contentType === 'dashboards') return folder.dashboards_count || 0
    if (contentType === 'samples') return folder.audit_reports_count || 0
    if (contentType === 'reports') return folder.reports_count || 0
    return folderItemCount(folder)
  }, [contentType, folderItemCount])

  const options = { refetchOnWindowFocus: false }

  // No `kind` — this is now the only index, so it wants every folder. Asking for kind: 'views'
  // here would silently hide any folder marked 'reports'. section_id scopes the counts so a module
  // chip can hide folders holding nothing from that module.
  const { data: foldersData, isSuccess: isFoldersSuccess } = useFolders({
    sectionId: params.section_id,
    options
  })

  // Every folder the user can reach, at every level. The API returns the whole tree flat, and the
  // page walks it — filtering a folder out of the listing must not drop its children with it.
  const folders = useMemo(() => {
    if (!isFoldersSuccess || !foldersData?.data) return []
    return foldersData.data
  }, [isFoldersSuccess, foldersData])

  const folderTree = useMemo(() => buildFolderTree(folders), [folders])

  // What a folder holds counting its subfolders: a folder whose work lives two levels down still
  // reads as full, and stays visible while a filter is on.
  const getFolderItemCount = useCallback(
    (folder) => rollupCount(folderTree, folder, directFolderItemCount),
    [folderTree, directFolderItemCount]
  )

  const activeFolder = useMemo(
    () => (activeFolderId ? folderTree.byId.get(activeFolderId) || null : null),
    [activeFolderId, folderTree]
  )

  const activeFolderAncestors = useMemo(
    () => (activeFolder ? folderAncestors(folderTree, activeFolder) : []),
    [activeFolder, folderTree]
  )

  // Only the level being looked at — the children of the open folder, or the top level. A search
  // instead looks at every level below that point, matching on the folder name, so "Cat" finds
  // "Caterpillar" however deep it is nested.
  const visibleFolders = useMemo(() => {
    const search = (params.search || '').trim().toLowerCase()
    const level = search
      ? (activeFolderId ? (activeFolder ? descendantFolders(folderTree, activeFolder) : []) : folders)
          .filter((folder) => (folder.name || '').toLowerCase().includes(search))
      : childFoldersOf(folderTree, activeFolderId)
    // A filter narrows the page to a subset — one module, one kind of item, or both. A folder with
    // nothing in that subset is noise: it says "folder" and opens onto an empty page. Unfiltered,
    // every folder shows including empty ones, so one just created is still findable.
    const filtering = Boolean(params.section_id) || contentType !== 'all'
    if (!filtering) return level
    return level.filter((folder) => getFolderItemCount(folder) > 0)
  }, [folders, folderTree, activeFolder, activeFolderId, params.search, params.section_id, contentType, getFolderItemCount])

  // Owner plus everyone who reaches the folder through a share on a folder above it.
  const folderSharedUserCount = useCallback((folder) => {
    if (!folder.is_owner) return 0
    return (folder.shared_users?.length || 0) + (folder.inherited_shared_users_count || 0)
  }, [])

  // Build params with folder filtering
  const apiParams = useMemo(() => {
    const p = { ...params }
    if (activeFolderId) {
      p.folder_id = activeFolderId
    } else if (!params.search) {
      p.unfiled = 'true'
    }
    return p
  }, [params, activeFolderId])

  // A move or a folder delete can change what any of the three lists contains, so all four caches
  // have to go. The two indexes this page replaced each invalidated only their own half.
  const invalidateAll = useCallback(() => {
    queryClient.invalidateQueries('folders')
    queryClient.invalidateQueries('dashboards')
    queryClient.invalidateQueries('reports')
    queryClient.invalidateQueries('reports-builder')
  }, [queryClient])

  const { mutate: moveItem } = useMoveItem({
    options: {
      onSuccess: () => {
        setMovingItemIds(new Set())
        invalidateAll()
      },
      onError: () => {
        setMovingItemIds(new Set())
        toast.error('Failed to move item')
      }
    }
  })

  // Reparenting a folder is a plain update — `parent_id` is what nests it.
  const { mutate: updateFolder } = useUpdateFolder({
    options: {
      onSuccess: () => {
        setMovingFolderIds(new Set())
        invalidateAll()
      },
      onError: () => {
        setMovingFolderIds(new Set())
        toast.error('Failed to move folder')
      }
    }
  })

  const fetchRelatedItems = useFetchRelatedItems()

  const { mutate: destroyFolder } = useDestroyFolder({
    options: {
      onSuccess: invalidateAll,
      onError: () => {
        toast.error('Failed to delete folder')
      }
    }
  })

  // DnD setup
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    })
  )

  // Folder navigation
  const navigateToFolder = useCallback((folder) => {
    setSearchParams({ folder_id: folder.id })
    setParams((prev) => ({ ...prev, page: 1 }))
  }, [setSearchParams])

  // Where a folder may be dropped: a folder the user owns, not itself, nothing nested inside it,
  // and with room for the whole branch under MAX_FOLDER_DEPTH. Cards ask before lighting up.
  const acceptsDrag = useCallback((targetFolder, activeData) => {
    if (activeData?.type !== 'folder') return true
    const dragged = folderTree.byId.get(activeData.folderId)
    return !!dragged && canNestUnder(folderTree, dragged, targetFolder)
  }, [folderTree])

  const moveFolder = useCallback((folderId, targetFolderId) => {
    const dragged = folderTree.byId.get(folderId)
    if (!dragged || !dragged.is_owner) return
    if ((dragged.parent_id || null) === (targetFolderId || null)) return

    if (targetFolderId) {
      const target = folderTree.byId.get(targetFolderId)
      // Dropping a folder on itself or on something inside it is a no-op, not an error worth saying.
      if (!target || target.id === folderId || isDescendantOf(folderTree, target, folderId)) return
      if (!target.is_owner) return

      if (folderDepth(folderTree, target) + 1 + subtreeHeight(folderTree, dragged) > MAX_FOLDER_DEPTH) {
        toast.error(`Folders can only be nested ${MAX_FOLDER_DEPTH} levels deep`)
        return
      }
    }

    setMovingFolderIds((prev) => new Set(prev).add(folderId))
    updateFolder({ id: folderId, parent_id: targetFolderId })
  }, [folderTree, updateFolder])

  const navigateToRoot = useCallback(() => {
    searchParams.delete('folder_id')
    setSearchParams(searchParams)
    setParams((prev) => ({ ...prev, page: 1 }))
  }, [searchParams, setSearchParams])

  const handleDeleteFolder = useCallback((folder) => {
    if (!window.confirm(`Delete folder "${folder.name}"?`)) return
    destroyFolder(folder.id)
  }, [destroyFolder])

  const { data: sections, isSuccess: isSectionsSuccess } = useSections({
    options: {}
  })

  // Filter sections based on user's section abbreviations
  const userSectionAbbreviations = useMemo(
    () => user?.section_abbreviations || [],
    [user?.section_abbreviations]
  )

  const userSections = useMemo(() => {
    return isSectionsSuccess
      ? sections.filter((section) =>
          userSectionAbbreviations.includes(section.abbreviation)
        )
      : []
  }, [isSectionsSuccess, sections, userSectionAbbreviations])

  // Get unique parent modules from user sections
  const parentModules = useMemo(() => {
    if (!isSectionsSuccess) return []
    return [
      ...new Set(
        userSections
          .map((s) => s.parent_module)
          .filter((pm) => pm && pm.trim() !== '')
      )
    ]
  }, [isSectionsSuccess, userSections])

  const {
    data: dashboards,
    isSuccess: isDashboardsSuccess,
    isLoading: isDashboardsLoading
  } = useDashboards({ params: apiParams, options })

  const {
    data: samples,
    isSuccess: isSamplesSuccess,
    isLoading: isSamplesLoading
  } = useSamples({ params: apiParams, options })

  const {
    data: reports,
    isSuccess: isReportsSuccess,
    isLoading: isReportsLoading
  } = useBuilderReports({
    params: apiParams,
    options: { ...options, enabled: canUseReports }
  })

  const hasAccessToSection = useCallback((sectionName) => {
    if (!isSectionsSuccess || !sections) return false
    const section = sections.find((s) => s.name === sectionName)
    if (!section) return false
    return userSectionAbbreviations.includes(section.abbreviation)
  }, [isSectionsSuccess, sections, userSectionAbbreviations])

  // The module chips resolve to a comma-joined list of section ids in params.section_id. Views and
  // samples are filtered server-side by it; reports are not (they have no section of their own),
  // so the same list is applied to their datasets here.
  const selectedSectionIds = useMemo(
    () => (params.section_id ? String(params.section_id).split(',').filter(Boolean) : []),
    [params.section_id]
  )

  const viewItems = useMemo(() => {
    if (!isDashboardsSuccess) return []
    return (dashboards.data || [])
      .filter((d) => hasAccessToSection(d.section_name))
      .map((d) => toViewItem(d, { sections }))
  }, [isDashboardsSuccess, dashboards, sections, hasAccessToSection])

  const sampleItems = useMemo(() => {
    if (!isSamplesSuccess) return []
    return (samples.data || [])
      .filter((s) => hasAccessToSection(s.section?.name))
      .map((s) => toSampleItem(s, { sections }))
  }, [isSamplesSuccess, samples, sections, hasAccessToSection])

  const reportItems = useMemo(() => {
    if (!canUseReports || !isReportsSuccess) return []
    return (reports.data || [])
      .map((r) => toReportItem(r))
      .filter((item) => matchesSectionFilter(item, selectedSectionIds))
  }, [canUseReports, isReportsSuccess, reports, selectedSectionIds])

  const itemsByType = useMemo(
    () => ({ dashboards: viewItems, samples: sampleItems, reports: reportItems }),
    [viewItems, sampleItems, reportItems]
  )

  const visibleItems = useMemo(() => {
    if (contentType === 'all') return [...viewItems, ...sampleItems, ...reportItems]
    return itemsByType[contentType] || []
  }, [contentType, viewItems, sampleItems, reportItems, itemsByType])

  const findItemData = useCallback((activeId) => {
    const parsed = parseDragId(activeId)
    if (!parsed) return null
    const item = visibleItems.find((i) => i.type === parsed.type && i.id === parsed.id)
    return { itemType: ITEM_TYPES[parsed.type].apiType, itemId: parsed.id, item }
  }, [visibleItems])

  const isItemShared = (item) => item?.sharedUsers?.length > 0

  // A folder inside a shared one is not private, even with no share of its own.
  const isFolderPrivate = (folder) =>
    (folder?.shared_users?.length || 0) === 0 && (folder?.inherited_shared_users_count || 0) === 0

  const executeMoveItem = useCallback((itemType, itemId, folderId, extraParams = {}) => {
    // Every item in the move gets the spinner, not just the one that was dragged — the linked ones
    // are leaving their cards behind too.
    const moving = [{ item_type: itemType, item_id: itemId }, ...(extraParams.related_items || [])]
    setMovingItemIds((prev) => {
      const next = new Set(prev)
      moving.forEach((m) => next.add(dragIdFor(typeKeyForApiType(m.item_type), m.item_id)))
      return next
    })
    moveItem({
      item_type: itemType,
      item_id: itemId,
      target_folder_id: folderId,
      ...extraParams
    })
  }, [moveItem])

  // Second half of a move: the shared-item-into-a-private-folder question, which has to be asked
  // after the linked-items one — the answer applies to every item in the move.
  const continueMove = useCallback((request) => {
    const { itemType, itemId, item, folderId, targetFolder, relatedItems } = request
    const extraParams = relatedItemsParam(relatedItems)

    if (targetFolder && item && isItemShared(item) && isFolderPrivate(targetFolder)) {
      setPendingMove({
        itemType, itemId, itemName: item.name, folderId, folderName: targetFolder.name,
        sharedUserCount: item.sharedUsers.length,
        folderItemCount: folderItemCount(targetFolder),
        extraParams
      })
      return
    }

    executeMoveItem(itemType, itemId, folderId, extraParams)
  }, [executeMoveItem, folderItemCount])

  // First half of a move: ask the API what else belongs with this item. Views, Samples and Reports
  // are filed independently, so moving one of them alone splits a report across folders — offer to
  // take the rest along. Only when landing IN a folder: dropping on "All Items" is an unfile.
  const beginMove = useCallback(async (request) => {
    const { itemType, itemId, folderId, targetFolder } = request
    if (!targetFolder) {
      continueMove(request)
      return
    }

    const dragId = dragIdFor(typeKeyForApiType(itemType), itemId)
    setMovingItemIds((prev) => new Set(prev).add(dragId))

    let related = []
    try {
      const response = await fetchRelatedItems({ item_type: itemType, item_id: itemId })
      // Anything already sitting in the destination has nothing to move.
      related = (response?.data || []).filter((candidate) => candidate.folder_id !== folderId)
    } catch {
      // A failed lookup must not swallow the move the user asked for — carry on without the prompt.
    }

    // Only this lookup's id: a second drop while an earlier move is still in flight must not
    // un-dim the card that move is still working on.
    setMovingItemIds((prev) => {
      const next = new Set(prev)
      next.delete(dragId)
      return next
    })

    if (related.length === 0) {
      continueMove(request)
      return
    }

    setPendingLinkedMove({ ...request, relatedItems: related })
  }, [continueMove, fetchRelatedItems])

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event
    if (!over || !active) return

    const activeId = String(active.id)
    const overId = String(over.id)

    if (!isFolderDropId(overId)) return

    // null for the "All Items" breadcrumb, which means out of every folder.
    const folderId = parseFolderDropId(overId)

    if (active.data?.current?.type === 'folder') {
      moveFolder(active.data.current.folderId, folderId)
      return
    }

    // Do not allow dropping into folders without edit access
    const targetFolder = folderId ? folderTree.byId.get(folderId) : null
    if (targetFolder && !targetFolder.is_owner && !targetFolder.is_editor) return
    if (folderId && !targetFolder) return

    const itemData = findItemData(activeId)
    if (!itemData) return

    const { itemType, itemId, item } = itemData

    // Only allow moving items the user owns
    if (item && item.userId !== user.id) return

    beginMove({ itemType, itemId, item, folderId, targetFolder })
  }, [folderTree, findItemData, beginMove, moveFolder, user.id])

  const activeTypes = contentType === 'all'
    ? ['dashboards', 'samples', ...(canUseReports ? ['reports'] : [])]
    : [contentType]

  const successByType = {
    dashboards: isDashboardsSuccess,
    samples: isSamplesSuccess,
    reports: !canUseReports || isReportsSuccess
  }
  const loadingByType = {
    dashboards: isDashboardsLoading,
    samples: isSamplesLoading,
    reports: canUseReports && isReportsLoading
  }
  const metaByType = {
    dashboards: isDashboardsSuccess ? dashboards.meta : { pages: 0, total: 0 },
    samples: isSamplesSuccess ? samples.meta : { pages: 0, total: 0 },
    reports: isReportsSuccess ? reports.meta : { pages: 0, total: 0 }
  }

  const isSuccess = activeTypes.every((t) => successByType[t])
  const isLoading = activeTypes.some((t) => loadingByType[t])
  // Under a type tab the footer describes that one list. Under "All Items" the page stacks page N
  // of each list, so the pager has to reach the deepest of them: `pages` is the largest page count
  // in play and `total` the sum. Reading Views' meta alone stranded everything past the first page
  // of the other lists — a folder of 100 reports and no views showed a single page, with the other
  // 88 reports unreachable. The row range is still approximate while several lists are stacked
  // (each contributes up to perPage rows to a page); an exact one needs a single server-side index
  // for all three.
  const activeMeta = contentType === 'all'
    ? activeTypes.reduce((acc, type) => ({
      pages: Math.max(acc.pages, metaByType[type]?.pages || 0),
      total: acc.total + (metaByType[type]?.total || 0)
    }), { pages: 0, total: 0 })
    : metaByType[contentType]

  // The folder being viewed rides along on the item's URL so its back link can land back here
  // rather than at the top of the index — see dashboard/folders/folder-context.
  const navigateToItem = useCallback((item) => {
    navigate(withFolderContext(item.path, activeFolderId))
  }, [navigate, activeFolderId])

  const toggleParentModule = (parentModule) => {
    setSelectedParentModule((prev) => {
      if (prev === parentModule) {
        return ''
      } else {
        return parentModule
      }
    })
  }

  // Reset page when switching content type
  useEffect(() => {
    setParams((prev) => ({ ...prev, page: 1 }))
  }, [contentType])

  useEffect(() => {
    if (selectedParentModule) {
      // Get all section IDs that match the selected parent module
      const sectionIds = userSections
        .filter((s) => s.parent_module === selectedParentModule)
        .map((s) => s.id)
        .join(',')

      setParams((prev) => ({
        ...prev,
        section_id: sectionIds,
        page: 1
      }))
    } else {
      setParams((prev) => ({
        ...prev,
        section_id: '',
        page: 1
      }))
    }
  }, [selectedParentModule, userSections])

  useEffect(() => {
    // Adjust perPage when switching view modes if current value is not in the new options
    const gridOptions = [12, 24, 36]
    const listOptions = [10, 25, 50, 100]
    const currentOptions = viewMode === 'grid' ? gridOptions : listOptions

    if (!currentOptions.includes(params.perPage)) {
      setParams((prev) => ({
        ...prev,
        perPage: currentOptions[0],
        page: 1
      }))
    }
  }, [viewMode, params.perPage])

  useEffect(() => {
    // If present, render the 'toast' message set by a section's dashboard#show that has no dashboards.
    const toastMessage = localStorage.getItem('toast')

    if (toastMessage) {
      toast.info(toastMessage, {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: true,
        progress: undefined,
        theme: 'light'
      })
      localStorage.removeItem('toast')
    }
  }, [])

  // "Remove from folder" is only offered inside a folder the user can write to, and only on items
  // they created: a folder shared as editor collects other people's work, and folders#move_item
  // refuses to move an item its caller does not own ("You can only move items you created").
  const canUnfile = (item) =>
    activeFolderId && activeFolder?.is_owner !== false && item.userId === user.id

  const removeFromFolderItem = (item) => (
    <Dropdown.Item
      onClick={() => moveItem({ item_type: ITEM_TYPES[item.type].apiType, item_id: item.id })}
    >
      Remove from folder
    </Dropdown.Item>
  )

  // folders#move_item refuses items the caller did not create, so "Move to folder…" is only on those.
  const canMove = (item) => item.userId === user.id

  const moveToItem = (item) => (
    <Dropdown.Item onClick={() => setMovingItem(item)}>
      <FontAwesomeIcon icon={faFolder} fixedWidth className="me-2 text-muted" />
      Move to folder…
    </Dropdown.Item>
  )

  // The menu's own folder actions: Move to folder… wherever the user owns the item, and the one-click
  // Remove from folder inside a folder.
  const folderActionItems = (item) => (
    <>
      {canMove(item) && moveToItem(item)}
      {canUnfile(item) && removeFromFolderItem(item)}
    </>
  )

  // Samples have no actions of their own here beyond filing.
  const sampleActionsMenu = (item) => (
    <Dropdown>
      <Dropdown.Toggle
        variant="link"
        className="text-muted p-1 border-0 rounded"
        bsPrefix="btn"
        data-testid={`item-menu-${item.id}`}
        style={{ lineHeight: 1 }}
        aria-label="Item actions"
        // dnd-kit claims pointerdown on the draggable wrapper; without this the dropdown never
        // opens on a card. ReportActionsMenu does the same for the same reason.
        onPointerDown={(e) => e.stopPropagation()}
      >
        <FontAwesomeIcon icon={faEllipsisV} />
      </Dropdown.Toggle>
      <Dropdown.Menu>{folderActionItems(item)}</Dropdown.Menu>
    </Dropdown>
  )

  // Views carry the same actions as their show page; the folder actions ride at the top when they apply.
  const viewActionsMenu = (item) => (
    <ViewActionsMenu dashboard={item.raw}>
      {(canMove(item) || canUnfile(item)) && (
        <>
          {folderActionItems(item)}
          <Dropdown.Divider />
        </>
      )}
    </ViewActionsMenu>
  )

  const itemMenu = (item) => {
    if (item.type === 'report') return <ReportActionsMenu report={item.raw} />
    if (item.type === 'view') return viewActionsMenu(item)
    return canMove(item) || canUnfile(item) ? sampleActionsMenu(item) : null
  }

  // A sample that isn't attached to a view is worth calling out; so is the view it belongs to.
  const sampleAttachmentIcon = (item, keyPrefix) => (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id={`tooltip-${keyPrefix}-${item.id}`}>
          {item.dashboard ? `Attached to ${item.dashboard.name}` : 'This sample is not attached to a view'}
        </Tooltip>
      }
    >
      <span>
        <FontAwesomeIcon
          icon={item.dashboard ? faLayerGroup : faUnlink}
          className="text-muted"
          style={{ fontSize: '0.85rem' }}
        />
      </span>
    </OverlayTrigger>
  )

  const itemActions = (item) => (
    <>
      {item.type === 'sample' && sampleAttachmentIcon(item, 'card')}
      {item.type === 'report' && <ReportLockBadge report={item.raw} iconOnly />}
      {itemMenu(item)}
    </>
  )

  const renderListRow = (item) => (
    <DraggableRow
      key={item.dragId}
      id={item.dragId}
      isMoving={movingItemIds.has(item.dragId)}
      disabled={activeFolder?.is_owner === false || item.userId !== user.id}
      isOwner={item.userId === user.id}
      onClick={() => navigateToItem(item)}
    >
      <td>
        <div className="d-flex align-items-center gap-2">
          <FontAwesomeIcon
            icon={ITEM_TYPES[item.type].icon}
            className="text-muted"
            style={{ fontSize: '0.8rem' }}
            title={ITEM_TYPES[item.type].label}
          />
          {item.type === 'sample' && sampleAttachmentIcon(item, 'row')}
          <div>
            <div className="d-flex align-items-center gap-2">
              {item.name}
              {item.type === 'report' && <ReportLockBadge report={item.raw} iconOnly />}
            </div>
            {item.description && (
              <div className="text-muted small">{item.description}</div>
            )}
          </div>
        </div>
      </td>
      <td>{item.sectionLabel || item.sectionName}</td>
      <td>{item.userFullName}</td>
      <td
        onClick={(e) => e.stopPropagation()}
        // dnd-kit claims pointerdown on the row; without this the dropdown never opens.
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="d-flex align-items-center gap-2">
          {itemMenu(item)}
        </div>
      </td>
    </DraggableRow>
  )

  const renderGridCard = (item) => (
    <Col key={item.dragId}>
      <DraggableItem
        id={item.dragId}
        isMoving={movingItemIds.has(item.dragId)}
        disabled={activeFolder?.is_owner === false || item.userId !== user.id}
        isOwner={item.userId === user.id}
      >
        <ItemCard item={item} onOpen={navigateToItem} actions={itemActions(item)} />
      </DraggableItem>
    </Col>
  )

  const emptyLabel = {
    samples: 'No Samples Found',
    reports: 'No Reports Found',
    dashboards: 'No Views Created',
    all: 'Nothing Here Yet'
  }[contentType]
  // Subfolders show inside a folder too — that's the extra depth. While searching, the listing holds
  // the folders whose names match, alongside the matching items.
  const showFolders = isFoldersSuccess && visibleFolders.length > 0
  // Where the Create menu files what it makes: the folder being viewed, but only one the user could
  // also drag an item into — the same owner-or-editor rule handleDragEnd applies. Viewing someone
  // else's read-only folder, a new View is yours and unfiled rather than dropped into their folder.
  const createInFolder = useMemo(() => {
    if (!activeFolder) return null
    return activeFolder.is_owner || activeFolder.is_editor ? activeFolder : null
  }, [activeFolder])

  // The full path, so the Create dialog can say "Audits / 2026 / Q1" rather than a bare "Q1" that
  // could be any of four folders with that name.
  const createInFolderPath = useMemo(
    () => (createInFolder ? folderPath(folderTree, createInFolder) : ''),
    [folderTree, createInFolder]
  )

  // On a direct load of /?folder_id=X the folder list has not arrived yet, so `activeFolder` is
  // still null. Rendering the button then labels it "New Folder" and creates at the top level —
  // the user asks for a subfolder and gets a root folder. Wait for the folder to resolve.
  const folderContextReady = !activeFolderId || !!activeFolder
  const canAddSubfolder = folderContextReady && (!activeFolder || activeFolder.is_owner === true)
  const atMaxFolderDepth = !!activeFolder && folderDepth(folderTree, activeFolder) >= MAX_FOLDER_DEPTH
  const showEmptyState = isSuccess && visibleItems.length === 0 && !showFolders
  // Only the Views empty state has an obvious next action; samples come from a view, and a report
  // is a beta feature not everyone has.
  const showCreatePrompt = contentType === 'dashboards' || contentType === 'all'

  return (
    <Page title="Views">
      <PageTitle />
      <Row className="mt-5">
        <Col>
          <Search params={params} setParams={setParams} />
        </Col>
        <Col sm="auto">
          <div className="d-flex gap-2">
            <ButtonGroup className="shadow-sm rounded-start rounded-end">
              <Button
                variant="light"
                className={`py-1.5 px-3 ${
                  viewMode === 'grid' ? 'tabs-active' : 'bg-white'
                }`}
                onClick={() => setViewMode('grid')}
                aria-label="Grid view"
              >
                <FontAwesomeIcon icon={faTh} />
              </Button>
              <Button
                variant="light"
                className={`py-1.5 px-3 ${
                  viewMode === 'list' ? 'tabs-active' : 'bg-white'
                }`}
                onClick={() => setViewMode('list')}
                aria-label="List view"
              >
                <FontAwesomeIcon icon={faList} />
              </Button>
            </ButtonGroup>
            <Dropdown>
              <Dropdown.Toggle variant="light" className="shadow-sm bg-white">
                {CONTENT_TYPES[contentType].icon && (
                  <FontAwesomeIcon icon={CONTENT_TYPES[contentType].icon} className="me-1" />
                )}
                {CONTENT_TYPES[contentType].label}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {Object.entries(CONTENT_TYPES)
                  .filter(([key]) => key !== 'reports' || canUseReports)
                  .map(([key, { label, icon }]) => (
                    <Dropdown.Item
                      key={key}
                      active={contentType === key}
                      onClick={() => setContentType(key)}
                    >
                      {icon && <FontAwesomeIcon icon={icon} className="me-1" />} {label}
                    </Dropdown.Item>
                  ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </Col>
        <Col>
          <div className="text-end d-flex justify-content-end align-items-center gap-2">
            {canAddSubfolder && (
              <CreateFolderModal parentFolder={activeFolder} atMaxDepth={atMaxFolderDepth} />
            )}
            <CreateMenu folder={createInFolder} folderPath={createInFolderPath} />
          </div>
        </Col>
      </Row>

      {parentModules.length > 0 && (
        <Row className="mt-3">
          <Col>
            <div className="d-flex flex-wrap gap-2">
              {parentModules.map((parentModule) => (
                <Card
                  key={parentModule}
                  className={`view-card ${
                    selectedParentModule === parentModule
                      ? 'border-primary bg-light'
                      : ''
                  }`}
                  onClick={() => toggleParentModule(parentModule)}
                  style={{ cursor: 'pointer' }}
                >
                  <Card.Body className="text-center py-2 px-3">
                    <div className="small fw-semibold d-flex align-items-center gap-2">
                      {selectedParentModule === parentModule && (
                        <FontAwesomeIcon
                          icon={faCheck}
                          className="text-primary"
                          style={{ fontSize: '0.75rem' }}
                        />
                      )}
                      {parentModule}
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          </Col>
        </Row>
      )}

      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        {/* Inside the DndContext on purpose: each crumb is a drop target, which only works when
            the droppable is registered under the same context as the draggables. */}
        {activeFolder && (
          <Row className="mt-3">
            <Col>
              <FolderBreadcrumb
                folder={activeFolder}
                ancestors={activeFolderAncestors}
                onNavigateHome={navigateToRoot}
                onNavigateToFolder={navigateToFolder}
              />
            </Col>
          </Row>
        )}

        <Row className="mt-4 pb-4">
          <Col>
            {viewMode === 'list' && (
              <>
                <Table
                  responsive
                  hover
                  className="border shadow-sm mt-2 bg-white"
                  size="sm"
                >
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Section</th>
                      <th>Created By</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading && <TableLoading columns={2} />}

                    {showFolders && visibleFolders.map((folder) => (
                      <FolderListRow
                        key={`folder-${folder.id}`}
                        folder={folder}
                        onClick={navigateToFolder}
                        onEdit={setEditingFolder}
                        onDelete={handleDeleteFolder}
                        onShare={(folder) => setSharingFolderId(folder.id)}
                        onMove={setMovingFolder}
                        itemCount={getFolderItemCount(folder)}
                        subfolderCount={folder.subfolders_count || 0}
                        sharedUserCount={folderSharedUserCount(folder)}
                        isMoving={movingFolderIds.has(folder.id)}
                        acceptsDrag={acceptsDrag}
                      />
                    ))}

                    {showEmptyState && (
                      <tr>
                        <td colSpan="4" className="text-center py-4">
                          <div className="mb-2 h6">{emptyLabel}</div>
                          {showCreatePrompt && (
                            <>
                              <MutedText as="div" lighter className="mb-3">
                                Get started by creating a new view
                              </MutedText>
                              <CreateMenu folder={createInFolder} folderPath={createInFolderPath} />
                            </>
                          )}
                        </td>
                      </tr>
                    )}

                    {isSuccess && visibleItems.map((item) => renderListRow(item))}
                  </tbody>
                </Table>
                {isSuccess && (
                  <TableFooter
                    meta={activeMeta}
                    params={params}
                    setParams={setParams}
                    perPageOptions={[10, 25, 50, 100]}
                  />
                )}
              </>
            )}

            {viewMode === 'grid' && (
              <>
                {isLoading && (
                  <div className="text-center py-5">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}
                {showEmptyState && (
                  <div className="text-center py-5">
                    <div className="mb-2 h6">{emptyLabel}</div>
                    {showCreatePrompt && (
                      <>
                        <MutedText as="div" lighter className="mb-3">
                          Get started by creating a new view
                        </MutedText>
                        <CreateMenu folder={createInFolder} folderPath={createInFolderPath} />
                      </>
                    )}
                  </div>
                )}

                {showFolders && (
                  <Row className="g-3 mt-2">
                    {visibleFolders.map((folder) => (
                      <FolderCard
                        key={`folder-${folder.id}`}
                        folder={folder}
                        onClick={navigateToFolder}
                        onEdit={setEditingFolder}
                        onDelete={handleDeleteFolder}
                        onShare={(folder) => setSharingFolderId(folder.id)}
                        onMove={setMovingFolder}
                        itemCount={getFolderItemCount(folder)}
                        subfolderCount={folder.subfolders_count || 0}
                        sharedUserCount={folderSharedUserCount(folder)}
                        isMoving={movingFolderIds.has(folder.id)}
                        acceptsDrag={acceptsDrag}
                      />
                    ))}
                  </Row>
                )}

                {isSuccess && visibleItems.length > 0 && (
                  <Row xs={1} sm={2} lg={3} xl={4} className="g-4 mt-2">
                    {visibleItems.map((item) => renderGridCard(item))}
                  </Row>
                )}
                {isSuccess && (
                  <div className="mt-4">
                    <TableFooter
                      meta={activeMeta}
                      params={params}
                      setParams={setParams}
                      perPageOptions={[12, 24, 36]}
                    />
                  </div>
                )}
              </>
            )}
          </Col>
        </Row>
      </DndContext>

      <EditFolderModal
        folder={editingFolder}
        show={!!editingFolder}
        onHide={() => setEditingFolder(null)}
      />

      <ShareFolderModal
        folder={folderTree.byId.get(sharingFolderId) || null}
        show={!!sharingFolderId}
        onHide={() => setSharingFolderId(null)}
      />

      <MoveFolderModal
        folder={movingFolder}
        folders={folders}
        show={!!movingFolder}
        onHide={() => setMovingFolder(null)}
      />

      {/* The same move a drop onto a folder makes — linked items and the private-folder question included. */}
      <MoveToFolderModal
        show={!!movingItem}
        onHide={() => setMovingItem(null)}
        itemName={movingItem?.name}
        currentFolderId={movingItem?.raw?.folder_id}
        folders={folders}
        onMove={(folderId) => {
          const item = movingItem
          setMovingItem(null)
          beginMove({
            itemType: ITEM_TYPES[item.type].apiType,
            itemId: item.id,
            item,
            folderId,
            targetFolder: folderId ? folderTree.byId.get(folderId) : null
          })
        }}
      />

      <MoveToPrivateFolderModal
        show={!!pendingMove}
        onHide={() => setPendingMove(null)}
        itemName={pendingMove?.itemName}
        folderName={pendingMove?.folderName}
        sharedUserCount={pendingMove?.sharedUserCount}
        folderItemCount={pendingMove?.folderItemCount}
        isLoading={movingItemIds.size > 0}
        onShareFolder={() => {
          const { itemType, itemId, folderId, extraParams } = pendingMove
          setPendingMove(null)
          executeMoveItem(itemType, itemId, folderId, { ...extraParams, share_folder: true })
        }}
        onMakePrivate={() => {
          const { itemType, itemId, folderId, extraParams } = pendingMove
          setPendingMove(null)
          executeMoveItem(itemType, itemId, folderId, { ...extraParams, remove_item_sharing: true })
        }}
      />

      <MoveLinkedItemsModal
        show={!!pendingLinkedMove}
        onHide={() => setPendingLinkedMove(null)}
        item={pendingLinkedMove?.item}
        itemApiType={pendingLinkedMove?.itemType}
        folderName={pendingLinkedMove?.targetFolder?.name}
        relatedItems={pendingLinkedMove?.relatedItems}
        isLoading={movingItemIds.size > 0}
        onMoveSelected={(selected) => {
          const request = pendingLinkedMove
          setPendingLinkedMove(null)
          continueMove({ ...request, relatedItems: selected })
        }}
        onMoveOnly={() => {
          const request = pendingLinkedMove
          setPendingLinkedMove(null)
          continueMove({ ...request, relatedItems: [] })
        }}
      />
    </Page>
  )
}

export default Dashboard
